import { $isCodeNode } from "@lexical/code-core";
import { $isLinkNode } from "@lexical/link";
import { $findMatchingParent } from "@lexical/utils";
import { $getSelection, $isRangeSelection, type LexicalEditor, type RangeSelection } from "lexical";

export interface ToolbarState {
  visible: boolean;
  bold: boolean;
  italic: boolean;
  strikethrough: boolean;
  code: boolean;
  /** The URL of the link the selection sits in, or `null`. */
  link: string | null;
}

const HIDDEN: ToolbarState = {
  visible: false,
  bold: false,
  italic: false,
  strikethrough: false,
  code: false,
  link: null,
};

export interface ToolbarStore {
  subscribe(listener: () => void): () => void;
  getSnapshot(): ToolbarState;
}

const same = (a: ToolbarState, b: ToolbarState): boolean =>
  a.visible === b.visible &&
  a.bold === b.bold &&
  a.italic === b.italic &&
  a.strikethrough === b.strikethrough &&
  a.code === b.code &&
  a.link === b.link;

export type InlineState = Pick<ToolbarState, "bold" | "italic" | "strikethrough" | "code" | "link">;

/** The marks and the link at a text selection, a bare caret included. */
export function $readInline(selection: RangeSelection): InlineState {
  const link =
    $findMatchingParent(selection.anchor.getNode(), $isLinkNode) ??
    $findMatchingParent(selection.focus.getNode(), $isLinkNode);
  return {
    bold: selection.hasFormat("bold"),
    italic: selection.hasFormat("italic"),
    strikethrough: selection.hasFormat("strikethrough"),
    code: selection.hasFormat("code"),
    link: link === null ? null : link.getURL(),
  };
}

/** Whether there is text selected that a link could be put on: some, not blank, not code. */
export function $canLink(selection: RangeSelection): boolean {
  if (selection.isCollapsed() || selection.getTextContent().trim() === "") return false;
  return $findMatchingParent(selection.anchor.getNode(), $isCodeNode) === null;
}

function $read(): ToolbarState {
  const selection = $getSelection();
  if (!$isRangeSelection(selection) || !$canLink(selection)) return HIDDEN;
  return { visible: true, ...$readInline(selection) };
}

/**
 * Selection-derived UI state. Listeners fire only when the snapshot actually changes, so typing
 * with a collapsed caret never re-renders the toolbar.
 */
export function createToolbarStore(editor: LexicalEditor): ToolbarStore {
  let snapshot = HIDDEN;
  const listeners = new Set<() => void>();
  let unregister: (() => void) | null = null;

  const refresh = (): void => {
    const next = editor.isEditable() ? editor.getEditorState().read($read) : HIDDEN;
    if (same(snapshot, next)) return;
    snapshot = next;
    for (const listener of listeners) listener();
  };

  return {
    subscribe(listener) {
      listeners.add(listener);
      if (unregister === null) {
        const offUpdate = editor.registerUpdateListener(refresh);
        const offEditable = editor.registerEditableListener(refresh);
        unregister = () => {
          offUpdate();
          offEditable();
        };
        refresh();
      }
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) {
          unregister?.();
          unregister = null;
        }
      };
    },
    getSnapshot: () => snapshot,
  };
}
